import React, { useState } from "react";
import Modal from "./Modal";
import { Plus } from "lucide-react";
import SearchableSelect from "./SearchableSelect";
import { formatNumberWithSeparator } from "../utils/numberFormat";

export default function AddTransactionModal({ isOpen, onClose, onSubmit }) {
  const [formData, setFormData] = useState({
    category: "masuk",
    jenis: "PENDAPATAN",
    nominal: "",
    keterangan: "",
    lampiran: null,
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prev) => ({
        ...prev,
        lampiran: file,
      }));
    }
  };

  const handleCategoryChange = (category) => {
    setFormData((prev) => ({
      ...prev,
      category,
      jenis: category === "masuk" ? "PENDAPATAN" : "BIAYA",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setSubmitting(true);
      const success = await onSubmit({ ...formData });

      if (success === false) return;

      setFormData({
        category: "masuk",
        jenis: "PENDAPATAN",
        nominal: "",
        keterangan: "",
        lampiran: null,
      });
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const jenisOptions =
    formData.category === "masuk"
      ? [
          { value: "PENDAPATAN", label: "Pendapatan" },
          { value: "NON_PENDAPATAN", label: "Non-Pendapatan" },
        ]
      : [
          { value: "BIAYA", label: "Biaya" },
          { value: "NON_BIAYA", label: "Non-Biaya" },
        ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Tambah Mutasi"
      headerColor="bg-teal-600">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">
              Kategori <span className="text-rose-500">*</span>
            </label>
            <SearchableSelect
              options={[
                { value: "masuk", label: "Uang Masuk" },
                { value: "keluar", label: "Uang Keluar" },
              ]}
              value={formData.category}
              onChange={handleCategoryChange}
              placeholder="Contoh: Uang Masuk"
              displayKey="label"
              valueKey="value"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">
              Jenis <span className="text-rose-500">*</span>
            </label>
            <SearchableSelect
              options={jenisOptions}
              value={formData.jenis}
              onChange={(value) =>
                handleChange({ target: { name: "jenis", value } })
              }
              placeholder="Contoh: Non-Pendapatan"
              displayKey="label"
              valueKey="value"
            />
          </div>
        </div>

        {/* Nominal */}
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1.5">
            Nominal <span className="text-rose-500">*</span>
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-500 font-medium text-sm">
              Rp
            </span>
            <input
              type="text"
              name="nominal"
              value={formatNumberWithSeparator(formData.nominal)}
              onChange={(e) => {
                const rawValue = e.target.value.replace(/\./g, "");
                setFormData((prev) => ({ ...prev, nominal: rawValue }));
              }}
              placeholder="0"
              className="w-full pl-10 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent focus:bg-white transition-colors"
              required
            />
          </div>
        </div>

        {/* Keterangan */}
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1.5">
            Keterangan
          </label>
          <textarea
            name="keterangan"
            value={formData.keterangan}
            onChange={handleChange}
            placeholder="Masukan keterangan disini..."
            rows="4"
            className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent focus:bg-white transition-colors resize-none"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1.5">
            Lampiran
          </label>
          <div className="border-2 border-dashed border-slate-200 bg-slate-50/40 rounded-xl p-6 text-center hover:border-teal-400 hover:bg-teal-50/40 transition-colors">
            <input
              type="file"
              id="mutation-file-upload"
              onChange={handleFileChange}
              accept=".png,.jpg,.jpeg,.pdf"
              className="hidden"
            />
            <label htmlFor="mutation-file-upload" className="cursor-pointer">
              <div className="flex flex-col items-center">
                <div className="mb-2">
                  <Plus className="w-10 h-10 text-slate-400 mx-auto mb-2" />
                </div>
                <p className="text-sm text-slate-600">
                  <span className="text-teal-600 font-semibold">
                    Upload a file
                  </span>{" "}
                  or drag and drop
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  PNG, JPG, PDF up to 10MB
                </p>
              </div>
            </label>
            {formData.lampiran && (
              <p className="text-sm text-slate-700 mt-2 font-medium">
                Selected: {formData.lampiran.name}
              </p>
            )}
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end pt-2">
          <button
            type="submit"
            disabled={submitting}
            className="px-5 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium text-sm shadow-sm shadow-teal-600/20 disabled:opacity-60">
            {submitting ? "Menyimpan..." : "Simpan Data"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
